import { InlineValuePopover, PopoverList, Switch } from "@xola/ui-kit";
import { DurationUnit } from "common";
import { isNil } from "lodash";
import React, { ReactNode, useMemo } from "react";
import { CutoffType, CutoffTypeCopy, Experience, ScheduleType } from "types";
import { TimePickerPopover } from "../../Common/TimePickerPopover";
import { TimePickerText } from "./TimePickerText";
import { useVisibility } from "@/hooks/useVisibility";

interface AvailabilityRestrictionsProps {
    values: Experience;
    onChange: (values: Experience) => void;
    errors: any;
}

enum CutoffOptions {
    ALTERNATE_CUTOFF = "alternateCutoff",
    FUTURE_CUTOFF = "futureCutoff",
}

export const AvailabilityRestrictions = ({ values, onChange, errors = {} }: AvailabilityRestrictionsProps) => {
    const isAllDaySchedule = values.scheduleType === ScheduleType.DAY;
    const isOpeningHoursScheduleEnabled = isAllDaySchedule && (values.openingHours?.length ?? 0) > 0;

    const futureCutoff = useMemo(
        () => ({
            value: values.futureCutoff?.value ?? 0,
            allowOverride: !!values.futureCutoff?.allowOverride,
        }),
        [values.futureCutoff],
    );

    const handleSwitchToggle = (value: boolean, type: CutoffOptions) => {
        switch (type) {
            case CutoffOptions.ALTERNATE_CUTOFF:
                if (value) {
                    onChange({ ...values, alternateCutoff: 0 });
                } else {
                    onChange({
                        ...values,
                        alternateCutoff: null,
                    });
                }

                break;
            case CutoffOptions.FUTURE_CUTOFF:
                if (value) {
                    onChange({ ...values, futureCutoff: { ...futureCutoff, value: 0 } });
                } else {
                    onChange({
                        ...values,
                        futureCutoff: null,
                    });
                }

                break;
            // no default
        }
    };

    const handleOverrideFutureCutoffToggle = (isChecked: boolean) => {
        onChange({ ...values, futureCutoff: { ...futureCutoff, allowOverride: isChecked } });
    };

    const [isCutoffTypeToggleOpen, { show: showCutoffToggle, hide: hideCutoffToggle }] = useVisibility(false);

    const handleCutoffTypeToggle = (value: CutoffType) => {
        onChange({ ...values, cutoffType: value });
    };

    return (
        <div className="flex space-x-8">
            <div className="flex flex-col space-y-4">
                <div className="flex flex-col space-y-2">
                    <span className="text-sm font-bold">Early Cutoff</span>
                    <div className="flex items-center space-x-2">
                        <span>
                            Prevent purchases that are made
                            <RestrictionTimePopover
                                value={values.cutoff ?? 0}
                                isError={false}
                                onApply={(value: number) => {
                                    if (value) {
                                        onChange({ ...values, cutoff: value });
                                    } else {
                                        onChange({ ...values, cutoff: null, alternateCutoff: null });
                                    }
                                }}
                            >
                                <TimePickerText timeInMinutes={values.cutoff ?? 0} />
                            </RestrictionTimePopover>
                            {values.cutoffType !== CutoffType.PRE_OPENING_END && "the event "}
                            <InlineValuePopover
                                isOpen={isCutoffTypeToggleOpen}
                                text={CutoffTypeCopy[values.cutoffType ?? CutoffType.PRE_START]}
                                error={false}
                                onClick={showCutoffToggle}
                                onClickOutside={hideCutoffToggle}
                            >
                                <PopoverList.Item
                                    name={CutoffTypeCopy[CutoffType.PRE_START]}
                                    onClickItem={() => handleCutoffTypeToggle(CutoffType.PRE_START)}
                                >
                                    {CutoffTypeCopy[CutoffType.PRE_START]}
                                </PopoverList.Item>
                                <PopoverList.Item
                                    name={CutoffTypeCopy[CutoffType.PRE_END]}
                                    onClickItem={() => handleCutoffTypeToggle(CutoffType.PRE_END)}
                                >
                                    {CutoffTypeCopy[CutoffType.PRE_END]}
                                </PopoverList.Item>
                                {isOpeningHoursScheduleEnabled && (
                                    <PopoverList.Item
                                        name={CutoffTypeCopy[CutoffType.PRE_OPENING_END]}
                                        onClickItem={() => handleCutoffTypeToggle(CutoffType.PRE_OPENING_END)}
                                    >
                                        {CutoffTypeCopy[CutoffType.PRE_OPENING_END]}
                                    </PopoverList.Item>
                                )}
                            </InlineValuePopover>
                        </span>
                    </div>
                    {!isNil(values.cutoff) && (
                        <div className="flex items-center space-x-2">
                            <Switch
                                size="medium"
                                isChecked={!isNil(values.alternateCutoff)}
                                onChange={(value: boolean) => handleSwitchToggle(value, CutoffOptions.ALTERNATE_CUTOFF)}
                            />
                            <span>
                                If an event has already confirmed purchases,
                                {isNil(values.alternateCutoff) ? (
                                    <span>&nbsp;use a different event start time</span>
                                ) : (
                                    <>
                                        &nbsp;prevent additional purchases within
                                        <RestrictionTimePopover
                                            value={values.alternateCutoff ?? 0}
                                            isError={!!errors.alternateCutoff}
                                            onApply={(value: number) => onChange({ ...values, alternateCutoff: value })}
                                        />
                                        of event start time
                                    </>
                                )}
                            </span>
                        </div>
                    )}
                </div>
                <div className="flex flex-col space-y-2">
                    <span className="text-sm font-bold">Future Cutoff</span>
                    <Switch.Group>
                        <div className="flex flex-col space-y-2">
                            <div className="flex items-center space-x-2">
                                <Switch
                                    data-testid="availability-restrictions-future-cutoff-switch-button"
                                    size="medium"
                                    isChecked={!isNil(values.futureCutoff?.value)}
                                    onChange={(value: boolean) =>
                                        handleSwitchToggle(value, CutoffOptions.FUTURE_CUTOFF)
                                    }
                                />
                                <span>
                                    Prevent purchases made
                                    {isNil(values.futureCutoff?.value) ? (
                                        <span>&nbsp;too far in advance</span>
                                    ) : (
                                        <>
                                            &nbsp;more than
                                            <RestrictionTimePopover
                                                value={values.futureCutoff?.value ?? 0}
                                                isError={!!errors.futureCutoff}
                                                onApply={(value: number) =>
                                                    onChange({
                                                        ...values,
                                                        futureCutoff: { ...futureCutoff, value: value },
                                                    })
                                                }
                                            />
                                            in advance
                                        </>
                                    )}
                                </span>
                            </div>
                            {!isNil(values.futureCutoff?.value) && (
                                <div>
                                    <span>
                                        <Switch
                                            data-testid="availability-restrictions-allow-future-cutoff-switch-button"
                                            isChecked={!!values.futureCutoff?.allowOverride}
                                            size="medium"
                                            onChange={handleOverrideFutureCutoffToggle}
                                        />
                                        <Switch.Label direction="right">
                                            Allow reservationists to override future cutoff for back office purchases
                                        </Switch.Label>
                                    </span>
                                </div>
                            )}
                        </div>
                    </Switch.Group>
                </div>
            </div>
        </div>
    );
};

interface RestrictionTimePopoverProps {
    value: number;
    isError: boolean;
    onApply: (value: number) => void;
    children?: ReactNode;
}

const RestrictionTimePopover = ({ value, isError, onApply, children }: RestrictionTimePopoverProps) => {
    return (
        <TimePickerPopover
            showArrow
            classNames={{
                text: "px-0.5 pb-0.5 mx-1 !text-black !border-black",
            }}
            value={value}
            isError={isError}
            preferredUnit={DurationUnit.MINUTE}
            onApply={onApply}
        >
            {children}
        </TimePickerPopover>
    );
};
